import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';
import { AppSettings } from '../app-settings';
import { BehaviorSubject, of, throwError } from 'rxjs';
import { CommonService } from './common.service';
import { formatDate } from '@angular/common';

/**
 * Etiqueta de estado de un evento: la clase de color (las .tag-* de
 * styles.scss), su icono y su texto.
 */
export interface StateTag {
  class: string;
  icon: string;
  text: string;
}

@Injectable({
  providedIn: 'root'
})
export class EventosService {

  // EventPanel
  private eventPanel = new BehaviorSubject<number>(null);
  public eventPanelStorage = this.eventPanel.asObservable();

  // Action Map
  private mapAction = new BehaviorSubject<any>(null);
  public mapActionObservable = this.mapAction.asObservable();

  // Save Map
  private mapSaveAction = new BehaviorSubject<any>(null);
  public mapSaveObservable = this.mapSaveAction.asObservable();

  // Create event
  stepObservable = new BehaviorSubject<any>(null);
  step = this.stepObservable.asObservable();

  // Filters events panel
  private filtersEventsPanel = new BehaviorSubject<any>(null);

  constructor(private http: HttpClient, private common: CommonService) { }

  setActionMap(action: any) {
    this.mapAction.next(action);
  }

  saveMapAction(save: any) {
    this.mapSaveAction.next(save);
  }

  setEventPanel(idEvent: number) {
    this.eventPanel.next(idEvent);
  }

  setEventsPanelFilters(searchText: string, placeSelected: any) {
    this.filtersEventsPanel.next({ searchText, placeSelected });
  }

  getEventsPanelFilters() {
    return this.filtersEventsPanel.getValue();
  }

  clearEventsPanelFilters() {
    this.filtersEventsPanel.next(null);
  }

  setStepCreate(step: string) {
    this.stepObservable.next(step);
  }

  getEventosExplorar(ciudad?: number) {
    const city = ciudad ? `id_city=${ciudad}&` : '';
    let url = `${AppSettings.API_URI_V2}/public/events?${city}publish=true&sort=start_date:asc`;
    return this.common.getData(url);
  }

  getEventsBest(city?: number) {
    let url = `${AppSettings.API_URI_V2}/best/events`;
    if (city) {
      url += `?id_city=${city}`
    }
    return this.common.getDataHydra(url);
  }

  getEventById(id: any) {
    const url = `${AppSettings.API_URI_V2}/events/${id}`;
    return this.common.getData(url);
  }

  getEventosSearchingClose(search: string, page: number, items: number, city?: number, filtros?: any, slug?: any) {
    const fecha = filtros?.find(value => value.type == 'fecha');
    const storageCity = sessionStorage.getItem('city');
    if (storageCity) {
      city = +storageCity;
    }

    let desde: any = formatDate(new Date(), 'yyyy-MM-dd', 'es-ES')
    let hasta = '';
    if (fecha) {
      if (fecha.desde < new Date()) {
        fecha.desde = new Date();
      }
      desde = formatDate(fecha.desde, 'yyyy-MM-dd', 'es-ES')
      hasta = formatDate(fecha.hasta, 'yyyy-MM-dd', 'es-ES')
    }
    const categories = filtros?.filter(value => value.type == 'category');
    const prices = filtros?.find(value => value.type == 'price');
    const orderBy = filtros?.find(value => value.type == 'order');
    const filterLocation = filtros?.find(value => value.type == 'location');

    let url = `${AppSettings.API_URI_V2}/public/events?`;

    url += `page=${page}`;
    url += `&per_page=${items}`;
    url += `&publish=true`;
    if (search && search.trim() !== '') {
      url += `&search=${encodeURIComponent(search || '')}`;
    }

    if (city) url += `&id_city=${city}`;
    if (slug) url += `&slug=${encodeURIComponent(slug)}`;
    if (filterLocation?.minLat) url += `&latitud=${filterLocation.minLat}`;
    if (filterLocation?.minLon) url += `&longitud=${filterLocation.minLon}`;
    if (prices?.min || prices?.min === 0) url += `&price_min=${prices.min}`;
    if (prices?.max) url += `&price_max=${prices.max}`;
    if (desde) url += `&date_from=${desde}`;
    if (hasta) url += `&date_to=${hasta}`;

    if (orderBy?.filter && orderBy?.mode) {
      url += `&sort=${encodeURIComponent(orderBy.filter)}:${encodeURIComponent(orderBy.mode)}`;
    } else {
      url += `&sort=start_date:asc`;
    }

    if (categories && categories.length > 0) {
      categories.forEach(category => {
        url += `&id_category[]=${category.id}`;
      });
    }

    return this.common.getData(url);
  }

  putEventos(eventoId: number, eventdata: any) {
    const url = `${AppSettings.API_URI_V2}/events/${eventoId}`;
    return this.http.put<any>(url, eventdata).pipe(
      map(data => {
        return data;
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  deleteEvent(eventoId: number) {
    const url = `${AppSettings.API_URI_V2}/events/${eventoId}`;
    return this.http.delete<any>(url).pipe(
      map(data => {
        return data;
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  togglePublishEvent(eventoId: number) {
    const url = `${AppSettings.API_URI_V2}/events/${eventoId}/toggle-publish`;
    return this.http.put<any>(url, {}).pipe(
      map(data => {
        return data;
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  putExtraImagesByEvent(eventId: number, payload: any) {
    const url = `${AppSettings.API_URI}/edit/extra/images/${eventId}`;
    return this.http.put<any>(url, payload).pipe(
      map(data => {
        return data;
      })
    );
  }

  /** Días que un evento luce la etiqueta de "¡Nuevo!" desde que se publica */
  private readonly NEW_EVENT_DAYS = 7;

  /**
   * Un evento es nuevo si se publicó hace poco. Se mide con publish_date, que
   * es lo que significa la etiqueta, pero esa columna hoy no la rellena nadie
   * (0 de 58 eventos en producción), así que se cae a created_at.
   *
   * Antes esto miraba un card.new que no existe en ninguna parte —ni columna,
   * ni modelo, ni respuesta de la API—, así que la etiqueta no llegaba a
   * pintarse nunca.
   */
  isNewEvent(card: any): boolean {
    const published = card?.publish_date ?? card?.created_at;
    if (!published) {
      return false;
    }
    const age = Date.now() - new Date(published).getTime();
    return age >= 0 && age < this.NEW_EVENT_DAYS * 24 * 60 * 60 * 1000;
  }

  /** La venta todavía no ha abierto: mismo criterio que el botón de compra */
  salesPending(event: any): boolean {
    return !!event?.start_sales && new Date(event.start_sales) > new Date();
  }

  /**
   * La etiqueta de estado del evento: la misma en la card del listado y en la
   * ficha, para que un color signifique lo mismo en los dos sitios. Solo se
   * pinta una, y este es el orden de prioridad.
   */
  getStateTag(event: any): StateTag | null {
    if (event?.sold_out === true) {
      return { class: 'tag-sold-out', icon: 'fa-cart-xmark', text: 'Sold out' };
    }
    if (this.salesPending(event)) {
      return { class: 'tag-soon', icon: 'fa-hourglass-start', text: 'Próximamente' };
    }
    const text = this.getTagString(event);
    if (!text) {
      return null;
    }
    if (this.datesEquals(this.nextSessionStart(event))) {
      return { class: 'tag-today', icon: 'fa-bell', text };
    }
    return { class: this.isNewEvent(event) ? 'tag-new' : 'tag-fire', icon: this.getTagIcon(event), text };
  }

  /** Primera sesión que no ha pasado. Ordena las sesiones del evento */
  private nextSessionStart(event: any): any {
    const sessions = event?.sessions;
    if (!sessions?.length) {
      return null;
    }
    sessions.sort((a: any, b: any) => {
      return new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
    });
    return sessions.find((session: any) => new Date(session.start_date) >= new Date())?.start_date ?? null;
  }

  // Design functions
  getTagString(card: any) {
    if (this.datesEquals(this.nextSessionStart(card))) {
      return '¡Es hoy!';
    } else if (this.isNewEvent(card)) {
      return '¡Nuevo!';
    } else if (card.numvaloraciones > 20 || card.mediavaloraciones > 4) {
      // Pendiente: no hay de dónde sacar las valoraciones, así que esta rama
      // no se cumple nunca (ver isNewEvent)
      return '¡Lo está petando!';
    }
  }

  getTagIcon(card: any) {
    if (this.datesEquals(this.nextSessionStart(card))) {
      return 'fa-bell';
    } else if (this.isNewEvent(card)) {
      return 'fa-sparkles';
    } else if (card.numvaloraciones > 20 || card.mediavaloraciones > 4) {
      return 'fa-fire-alt';
    }
  }

  /**
   * Margen que la venta puede sobrevivir al final del evento. Hace falta un
   * tope porque hay eventos con el fin de ventas olvidado meses por delante de
   * sus sesiones (en PROD, uno con la venta abierta a un año de su última
   * sesión), y sin él volverían a vender solos.
   *
   * Se cuenta desde el final del evento y no desde la sesión, porque un evento
   * de varios días puede tener una sola sesión el primer día (SDi Women Tech:
   * del 13 al 19 de febrero con una única sesión el 13) y ahí la venta tiene
   * que poder llegar hasta el final.
   */
  private readonly SALE_HOURS_AFTER_EVENT_END = 24;

  /**
   * Ventana de venta que abre el organizador a mano: mientras el fin de ventas
   * siga vigente la venta continúa abierta aunque la sesión ya haya empezado.
   * Es lo único que permite vender con el evento en marcha, y es la misma regla
   * que ya aplica el tótem (btns-event-totem: con end_sales no mira la hora de
   * las sesiones).
   */
  isSaleWindowOpen(event: any): boolean {
    const endMs = this.getWallClockTime(event?.end_sales);
    return !isNaN(endMs) && endMs > Date.now();
  }

  /**
   * ¿Sigue vendiéndose esta sesión aunque ya haya empezado? Solo con el fin de
   * ventas vigente y sin pasarse de la caducidad de la prórroga. No se cuenta
   * por días para que una sesión de las 23:00 se pueda vender de madrugada.
   */
  isSessionSaleExtended(event: any, session: any): boolean {
    if (!this.isSaleWindowOpen(event)) {
      return false;
    }
    const startMs = this.getWallClockTime(session?.start_date);
    if (isNaN(startMs)) {
      return false;
    }
    const nowMs = Date.now();
    return nowMs >= startMs && nowMs < this.getSaleExtensionLimit(event, startMs);
  }

  /**
   * Cuándo caduca la prórroga de una sesión ya empezada:
   * - Mientras haya una sesión posterior, al arrancar aquella. Así en un
   *   festival no se vende el día 1 estando en el día 3.
   * - En la última sesión, el margen desde el final del evento, que es lo que
   *   deja vender durante un evento de varios días con una sola sesión.
   */
  private getSaleExtensionLimit(event: any, sessionStartMs: number): number {
    const startsMs = (event?.sessions || [])
      .filter((session: any) => session?.active !== false)
      .map((session: any) => this.getWallClockTime(session?.start_date))
      .filter((ms: number) => !isNaN(ms));
    const nextStartMs = startsMs
      .filter((ms: number) => ms > sessionStartMs)
      .sort((a: number, b: number) => a - b)[0];
    if (nextStartMs !== undefined) {
      return nextStartMs;
    }
    // El final del evento a veces falta o se queda por detrás de la última
    // sesión, así que nunca se acorta la prórroga por debajo de ella.
    const eventEndMs = this.getWallClockTime(event?.end_date);
    const endMs = Math.max(isNaN(eventEndMs) ? sessionStartMs : eventEndMs, sessionStartMs);
    return endMs + this.SALE_HOURS_AFTER_EVENT_END * 60 * 60 * 1000;
  }

  /** ¿Le queda al evento alguna sesión empezada que todavía se pueda vender? */
  hasSessionSaleExtended(event: any): boolean {
    return (event?.sessions || []).some((session: any) =>
      session?.active !== false && this.isSessionSaleExtended(event, session)
    );
  }

  /**
   * Hora de la cadena tal cual, en local. Las fechas llegan con la hora que
   * escribió el organizador pero marcadas como UTC, así que se parte la cadena
   * en vez de usar new Date(), que la correría una zona horaria: un fin de
   * ventas puesto a la 01:00 cerraría a las 03:00 en horario de verano.
   */
  private getWallClockTime(value: string): number {
    const normalized = String(value || '').replace(' ', 'T');
    const [datePart, timePart] = [normalized.split('T')[0], normalized.split('T')[1] || '00:00:00'];
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hours)) {
      return NaN;
    }
    return new Date(year, month - 1, day, hours, minutes || 0).getTime();
  }

  datesEquals(fechaCard: any, format: string = 'd/M/yyyy') {
    let today = new Date();
    return this.ConvertDateToStringFormat(fechaCard, format) == today.toLocaleDateString();
  }

  ConvertDateToStringFormat(date: any, format: string = 'dd MMM'): string {
    if (date) {
      date = new Date(date);
      let myFormat = formatDate(date, format, 'es-ES', '+0000');
      return myFormat;
    }
    return null;
  }

  getDateString(fecha: any) {
    let stringHour = this.ConvertDateToStringFormat(fecha, 'HH:mm');
    let stringDate = 'Hoy';
    if (!this.datesEquals(fecha)) {
      stringDate = this.ConvertDateToStringFormat(fecha);
    }
    if (stringHour != '00:00') {
      stringDate += ' | ' + stringHour + 'h';
    }
    return stringDate;
  }

  getCity(ciudad: number) {
    let url = `${AppSettings.API_URI_V2}/cities/${ciudad}`;
    return this.http.get<any>(url).pipe(
      map(data => {
        return data;
      })
    );
  }

  getQrEvent(idEvent: number) {
    const formHeaders = {
      headers: new HttpHeaders({
        'Content-Type': 'image/svg+xml'
      })
    };
    let url = `${AppSettings.API_CHATBOT}/api/event/qr/${idEvent}`;
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(data => {
        return data;
      }), catchError(err => { return throwError(() => err); })
    );
  }

  getCountTicketsByEvent(idSession: number) {
    const url = `${AppSettings.API_URI_V2}/count/tickets/enabled/session/${idSession}`;
    return this.http.get(url).pipe(
      map(data => {
        return data;
      }), catchError(err => { return throwError(() => err); })
    );
  }

  getInfoCapacityEvent(session: number) {
    return this.http
      .get<any>(AppSettings.API_URI_V2 + '/info/capacity/session/' + session)
      .pipe(
        map((data) => {
          return data;
        })
      );
  }

  getInfoSalesByEvent(idEvent: number) {
    const url = `${AppSettings.API_URI_V2}/info/events/${idEvent}/tickets`;
    return this.http.get(url).pipe(
      map(data => {
        return data;
      }), catchError(err => { return throwError(() => err); })
    );
  }

  // El informe resumido en PDF, ya maquetado por la API. Viene como binario,
  // así que `responseType: 'blob'`: sin eso Angular intentaría parsearlo como
  // JSON y reventaría.
  downloadSalesSummaryPdf(idEvent: number) {
    const url = `${AppSettings.API_URI_V2}/info/events/${idEvent}/summary/pdf`;
    return this.http.get(url, { responseType: 'blob' }).pipe(
      map(data => {
        return data;
      }), catchError(err => { return throwError(() => err); })
    );
  }

  // Informe resumido del evento: totales y desglose por tipo de entrada y canal
  getSalesSummaryByEvent(idEvent: number) {
    const url = `${AppSettings.API_URI_V2}/info/events/${idEvent}/summary`;
    return this.http.get(url).pipe(
      map(data => {
        return data;
      }), catchError(err => { return throwError(() => err); })
    );
  }

  //V2
  getEventsBySpace(idSpace: number) {
    const url = `${AppSettings.API_URI_V2}/public/events?id_space=${idSpace}&sort=start_date:asc`;
    return this.http.get(url).pipe(
      map(data => {
        return data;
      }), catchError(err => { return throwError(() => err); })
    );
  }

  getMapEventBySession(idEvent: number, idSession: number) {
    const url = `${AppSettings.API_URI_V2}/events/${idEvent}/sessions/${idSession}/map`;
    return this.http.get(url).pipe(
      map(data => {
        return data;
      }), catchError(err => { return throwError(() => err); })
    );
  }

  // Info del pedido al que pertenece cada butaca ocupada de una sesión (solo admin).
  // Se usa en el editor de eventos para mostrar el pedido al hacer hover sobre una butaca.
  getEventSeatsOrders(idEvent: number, idSession: number) {
    const url = `${AppSettings.API_URI_V2}/admin/events/${idEvent}/sessions/${idSession}/seats-orders`;
    return this.http.get(url).pipe(
      map(data => {
        return data;
      }), catchError(err => { return throwError(() => err); })
    );
  }

  getSessionsByMonth(companyId: number, month: string) {
    const url = `${AppSettings.API_URI_V2}/companies/${companyId}/sessions?month=${month}`;
    return this.http.get(url).pipe(
      map(data => {
        return data;
      }), catchError(err => { return throwError(() => err); })
    );
  }

deleteImageEvent(eventId: number, imageId: number) {
  const url = `${AppSettings.API_URI_V2}/events/${eventId}/image/${imageId}`;
  return this.http.delete<any>(url).pipe(
    map(data => data),
    catchError(err => throwError(() => err))
  );
}
  checkCodePrivateEvent(event: number, code: string) {
    const url = `${AppSettings.API_URI_V2}/check/code/private/event/${event}`;
    return this.http.post(url, {code: code}).pipe(
      map(data => {
        return data;
      }), catchError(err => { return throwError(() => err); })
    );
  }

  // Cambiar estado del evento (stop_sales, cancelled, sold_out, deactivate, normal)
  changeEventState(eventId: number, state: string) {
    const url = `${AppSettings.API_URI_V2}/events/${eventId}/state`;
    return this.http.patch<any>(url, { state }).pipe(
      map(data => data),
      catchError(err => throwError(() => err))
    );
  }

}
